import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, NavLink, Link, useLocation } from "react-router-dom";
import Home from "./Home";
import AboutPage from "./About";
import ServicePage from "./ServicesPage";
import PortfolioPage from "./PortfolioPage";
import WhyPage from "./WhyUsPage";
import ContactUs from "./Contactus";
import Privacy from "./Privacy";
import ReqForm from "./ReqForm";
import ThankYou from "./Thank-you";
import { MdOutlineMenu } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";

import Logo from "./Logo";

const NavBar = () => {
  const [isListOpen, setIsListOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();

  // Scroll to top and update dynamic SEO metadata (Canonical, Title, Meta Description) on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsListOpen(false);

    // Route metadata definitions
    const routeSeoMap = {
      "/": {
        title: "Core Spectators | Custom Shopify Store Development, Mobile Apps & SaaS MVPs",
        canonical: "https://corespectators.com/",
        desc: "Core Spectators is a full-stack software and e-commerce engineering agency. We engineer bespoke high-converting Shopify 2.0 stores, cross-platform Flutter mobile apps, scalable SaaS MVPs, and autonomous AI workflow automations."
      },
      "/services": {
        title: "Software Engineering & Shopify Development Services | Core Spectators",
        canonical: "https://corespectators.com/Services",
        desc: "Custom Shopify 2.0 themes, headless Hydrogen stores, cross-platform Flutter/React Native mobile applications, SaaS MVP platforms, and AI workflow automation consulting."
      },
      "/portfolio": {
        title: "Client Case Studies & Engineering Portfolio | Core Spectators",
        canonical: "https://corespectators.com/Portfolio",
        desc: "Explore verified case studies: ProfitIQ AI financial mobile app, EduSaaS Pro multi-tenant LMS, CocoBeauty & Colderatti high-converting Shopify Plus stores."
      },
      "/about": {
        title: "About Core Spectators | Full-Stack Engineering & E-Commerce Agency",
        canonical: "https://corespectators.com/About",
        desc: "Learn about Core Spectators: our engineering leadership, enterprise NDA protection, 100% intellectual property ownership, and New York & Islamabad delivery teams."
      },
      "/why-us": {
        title: "Why Choose Us | Enterprise Software Architecture Standards | Core Spectators",
        canonical: "https://corespectators.com/Why-us",
        desc: "Discover why startups and enterprises choose Core Spectators: rapid 2-6 week delivery, 100% IP ownership, transparent communication, and zero lock-in."
      },
      "/contact": {
        title: "Contact Core Spectators | US & Global Engineering Offices",
        canonical: "https://corespectators.com/Contact",
        desc: "Contact Core Spectators engineering leadership for project estimates, technical discovery sessions, or RFP submissions. US: +1-332-378-8142 | PK: +92-333-5136060."
      },
      "/form": {
        title: "Get Project Quote & Architecture Scope Estimate | Core Spectators",
        canonical: "https://corespectators.com/Form",
        desc: "Use our interactive project requirements wizard to specify your Shopify, Flutter mobile app, or SaaS MVP features and receive a comprehensive scope breakdown within 24 hours."
      },
      "/privacy": {
        title: "Privacy Policy & Data Protection | Core Spectators",
        canonical: "https://corespectators.com/Privacy",
        desc: "Core Spectators privacy policy and data security practices. Learn how we safeguard client confidentiality, NDA compliance, and proprietary software data."
      },
      "/thank-you": {
        title: "Thank You | Core Spectators",
        canonical: "https://corespectators.com/thank-you",
        desc: "Thank you for reaching out to Core Spectators. Our software architects will review your project requirements and respond within 24 hours."
      }
    };

    const cleanPath = (location.pathname || "/").toLowerCase().replace(/\/+$/, "") || "/";
    const mappedKey = cleanPath === "/contact-us" ? "/contact" : cleanPath === "/get-quote" ? "/form" : cleanPath;
    const matchedSeo = routeSeoMap[mappedKey] || routeSeoMap["/"];

    // Update Title
    document.title = matchedSeo.title;

    // Update Canonical URL
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute("href", matchedSeo.canonical);

    // Update Hreflang Tags
    let hreflangEn = document.querySelector('link[rel="alternate"][hreflang="en"]');
    if (!hreflangEn) {
      hreflangEn = document.createElement("link");
      hreflangEn.setAttribute("rel", "alternate");
      hreflangEn.setAttribute("hreflang", "en");
      document.head.appendChild(hreflangEn);
    }
    hreflangEn.setAttribute("href", matchedSeo.canonical);

    let hreflangDef = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (!hreflangDef) {
      hreflangDef = document.createElement("link");
      hreflangDef.setAttribute("rel", "alternate");
      hreflangDef.setAttribute("hreflang", "x-default");
      document.head.appendChild(hreflangDef);
    }
    hreflangDef.setAttribute("href", matchedSeo.canonical);

    // Update Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", matchedSeo.desc);
    }
  }, [location.pathname]);

  // Close mobile menu on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsListOpen(false);
      }
    };
    if (isListOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isListOpen]);

  const toggleList = () => {
    setIsListOpen(!isListOpen);
  };

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/About" },
    { name: "Services", path: "/Services" },
    { name: "Portfolio", path: "/Portfolio" },
    { name: "Why Us", path: "/Why-us" },
    { name: "Contact Us", path: "/Contact" },
  ];

  return (
    <>
      <div className="2xl:container 2xl:mx-auto sticky top-0 z-50 bg-white shadow-md">
        <div className="flex items-center justify-between py-3 px-4 sm:px-7 max-w-7xl mx-auto">
          {/* Upgraded Vector Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <Logo className="h-[44px] sm:h-[50px] md:h-[54px] w-auto transition-transform duration-300 group-hover:scale-[1.02]" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2 lg:space-x-3">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg font-medium text-sm lg:text-base transition duration-150 ${isActive
                    ? "text-white bg-red-600 shadow-sm"
                    : "text-gray-700 hover:text-red-600 hover:bg-gray-50"
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}

            <Link
              to="/Form"
              className="ml-3 bg-red-600 hover:bg-red-700 text-white text-sm lg:text-base font-semibold px-4 py-2 rounded-lg shadow transition"
            >
              Get a Quote
            </Link>
          </nav>

          {/* Mobile Menu Toggle Button */}
          <div className="md:hidden flex items-center" ref={menuRef}>
            <button
              onClick={toggleList}
              aria-label="Toggle navigation menu"
              className="p-2 text-gray-700 hover:text-red-600 focus:outline-none"
            >
              {isListOpen ? <RxCross2 className="h-7 w-7" aria-hidden="true" /> : <MdOutlineMenu className="h-7 w-7" aria-hidden="true" />}
            </button>

            {/* Mobile Dropdown */}
            {isListOpen && (
              <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-xl py-3 px-6 z-50 animate-fadeIn">
                <ul className="flex flex-col space-y-2">
                  {navLinks.map((link) => (
                    <li key={link.path}>
                      <NavLink
                        to={link.path}
                        onClick={() => setIsListOpen(false)}
                        className={({ isActive }) =>
                          `block px-4 py-2.5 rounded-lg text-sm font-medium ${isActive
                            ? "text-white bg-red-600"
                            : "text-gray-700 hover:bg-gray-100"
                          }`
                        }
                      >
                        {link.name}
                      </NavLink>
                    </li>
                  ))}
                  <li className="pt-2">
                    <Link
                      to="/Form"
                      onClick={() => setIsListOpen(false)}
                      className="block text-center bg-red-600 text-white font-semibold py-2.5 px-4 rounded-lg hover:bg-red-700"
                    >
                      Get a Quote
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Application Main Landmark & Routes */}
      <main id="main-content" role="main" className="flex-grow w-full">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/About" element={<AboutPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/Services" element={<ServicePage />} />
          <Route path="/services" element={<ServicePage />} />
          <Route path="/Portfolio" element={<PortfolioPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/Why-us" element={<WhyPage />} />
          <Route path="/why-us" element={<WhyPage />} />
          <Route path="/Contact" element={<ContactUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/Privacy" element={<Privacy />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/Form" element={<ReqForm />} />
          <Route path="/form" element={<ReqForm />} />
          <Route path="/get-quote" element={<ReqForm />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
    </>
  );
};

export default NavBar;
